import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { colorsApi } from "../api/colors";
import { isValidHex, type PaletteColor } from "../catalog/colors";
import { ErrorNotice } from "../components/ui";

/**
 * Adding a colour the shop stocks but the palette does not have yet.
 *
 * Both values are the merchant's: the name is typed, and the swatch is picked
 * or entered as hex. Neither is derived from the other and neither is read from
 * the photograph — a colour taken from an image would be whatever the lighting
 * happened to be that day.
 *
 * The colour is saved to the shop's palette, not just used here, so it is a
 * normal selectable circle in every later Add and Edit workflow.
 */
export function AddNewColor({
  detectedName,
  onAdded,
}: {
  /** The folder name that could not be matched, offered as a starting point. */
  detectedName: string;
  onAdded: (color: PaletteColor) => void;
}) {
  const [open, setOpen] = useState(false);
  // Title-cased folder name as a suggestion only — the operator owns the final
  // wording, so it is editable and never saved without them seeing it.
  const [name, setName] = useState(() =>
    detectedName
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((part) => part[0]!.toUpperCase() + part.slice(1).toLowerCase())
      .join(" "),
  );
  const [hex, setHex] = useState("#808080");
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () => colorsApi.add({ name, swatch_hex: hex }),
    onSuccess: (color) => {
      // Every open workflow sees the new colour, not just this card.
      void queryClient.invalidateQueries({ queryKey: ["colors"] });
      onAdded(color);
      setOpen(false);
    },
  });

  const hexValid = isValidHex(hex);
  const nameValid = name.trim().length > 0;

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn--subtle btn--sm"
        onClick={() => setOpen(true)}
      >
        Add New Colour
      </button>
    );
  }

  return (
    <div className="addColor">
      <p className="addColor__intro">
        Folder name: <strong>{detectedName}</strong>
      </p>

      <label className="field field--inline">
        <span className="field__label">Colour name</span>
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Burnt Orange"
        />
      </label>

      <div className="addColor__swatchRow">
        <label className="field field--inline">
          <span className="field__label">Colour</span>
          <input
            type="color"
            className="addColor__picker"
            value={hexValid ? hex : "#808080"}
            onChange={(event) => setHex(event.target.value.toUpperCase())}
            aria-label="Pick colour"
          />
        </label>

        <label className="field field--inline">
          <span className="field__label">Hex value</span>
          <input
            className="input addColor__hex"
            value={hex}
            onChange={(event) => setHex(event.target.value)}
            spellCheck={false}
            aria-invalid={!hexValid}
          />
        </label>
      </div>

      {!hexValid ? (
        <p className="addColor__error" role="alert">
          Enter a colour as six hex digits, for example #C8621F.
        </p>
      ) : null}

      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <div className="addColor__actions">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={!hexValid || !nameValid || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save Colour"}
        </button>
        <button
          type="button"
          className="btn btn--subtle btn--sm"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
